import axios from 'axios';
import { AUTH_BASE_URL } from '../config/serverApiConfig';

/**
 * Password recovery and the two password-change routes.
 *
 * Every call here uses raw `axios` and lets errors propagate. It does
 * **not** go through `errorHandler`, which pops a generic "Request error" toast and returns
 * `{ success: false }`: these screens branch on the exact status (400 vs 401 vs 429 vs 503)
 * and put their own wording on a field, so a global toast would be both wrong and duplicated.
 *
 * It also does not go through `api()`. That instance's interceptor turns any 401 into a token
 * refresh followed by a redirect to `/login`, which is wrong for screens where a
 * 401 means "wrong code, try again"; it also reads `error.response.status` unguarded, so a
 * dropped connection throws inside the error handler.
 */

export const authHeaders = () => {
  const user = sessionStorage.getItem('auth');
  const tmpAuth = user ? JSON.parse(user).current : {};
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${tmpAuth?.access_token}`
  };
};

// ---------------------------------------------------------------------------
// The three post-sign-in flags
// ---------------------------------------------------------------------------

export type PostLoginFlag =
  | 'mfa_enrolment_required'
  | 'recovery_codes_required'
  | 'reenrol_totp_required'
  | 'must_change_password';

export interface PostLoginFlags {
  mfa_enrolment_required: boolean;
  recovery_codes_required: boolean;
  reenrol_totp_required: boolean;
  must_change_password: boolean;
}

/**
 * Every successful sign-in carries three flags, and `localStorage.auth.current` is the only
 * place they survive: `redux/auth/reducer.js` whitelists the fields it copies on
 * `REQUEST_SUCCESS` and silently drops everything it does not name, then `fetchUser`
 * re-dispatches without them. Reading the stored blob is also what `authHeaders` above does,
 * so this is the same source of truth the rest of the app already trusts.
 *
 * A corrupt or absent blob reads as "no flags", never as a thrown error inside a gate that
 * renders on every route.
 */
export const readPostLoginFlags = (): PostLoginFlags => {
  try {
    const stored = sessionStorage.getItem('auth');
    const current = stored ? JSON.parse(stored)?.current ?? {} : {};
    return {
      mfa_enrolment_required: !!current.mfa_enrolment_required,
      recovery_codes_required: !!current.recovery_codes_required,
      reenrol_totp_required: !!current.reenrol_totp_required,
      must_change_password: !!current.must_change_password
    };
  } catch {
    return {
      mfa_enrolment_required: false,
      recovery_codes_required: false,
      reenrol_totp_required: false,
      must_change_password: false
    };
  }
};

/**
 * Unset one flag once it has been satisfied, so the popup behind it does not return on the
 * next navigation. The server re-evaluates all three at the next sign-in, so this is a local
 * acknowledgement rather than a claim about server state.
 */
export const clearPostLoginFlag = (flag: PostLoginFlag): void => {
  try {
    const stored = sessionStorage.getItem('auth');
    if (!stored) return;
    const parsed = JSON.parse(stored);
    if (!parsed?.current || !(flag in parsed.current)) return;
    delete parsed.current[flag];
    sessionStorage.setItem('auth', JSON.stringify(parsed));
  } catch {
    // A blob we cannot parse is one we must not rewrite.
  }
};

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export interface ForgotPasswordVerifyResponse {
  /** Single-use, 5 minutes. Hold it in component state only. Never localStorage or the URL. */
  reset_ticket: string;
  expires_in: number;
}

/**
 * `/login` answers with one of a few bodies, all HTTP 200. The authenticator app is the only
 * second factor; the reply to a correct `otp` is a session, never a second challenge.
 * Discriminate in this order. `Access_token`, then `2fa_active`, then `2fa_required`, and
 * note that a *successful* body also carries `2fa_active: true`, which is why the token check
 * has to come first.
 */
export interface LoginResponse {
  access_token?: string;
  refresh_token?: string;
  email?: string;
  roles?: string[];
  first_name?: string;
  last_name?: string;
  userName?: string;
  /** TOTP challenge: re-POST `/login` with `otp`. The primary factor. */
  '2fa_active'?: boolean;
  '2fa_required'?: boolean;
  method?: 'totp';
  mfa_ticket?: string;
  expires_in?: number;
  /** The account holds neither factor. Blocking. */
  mfa_enrolment_required?: boolean;
  /** It holds a factor and **no unused codes**. Blocking, and the ordered successor of the
   *  flag above: the two are never both true. */
  recovery_codes_required?: boolean;
  /** This sign-in used a fallback, so the authenticator was removed. Non-blocking prompt. */
  reenrol_totp_required?: boolean;
  /** The account must change its password before proceeding. */
  must_change_password?: boolean;
}

export interface MfaStatus {
  /** Read live from Keycloak, not a cached column. Re-fetch rather than mutating locally. */
  totp: boolean;
  /**
   * Whether this installation makes an account with no factor enrol one. A server setting,
   * not a fact about the account. Absent on backends older than the setting: treat that as
   * `true`, which is what they do.
   */
  enrolment_enforced?: boolean;
}

/** What `GET /users/me/mfa/recovery-codes` reports. A count. Never the codes themselves. */
export interface RecoveryCodesStatus {
  remaining: number;
  total: number;
}

/**
 * The confirm routes hand back the recovery-code set **only when the account holds no unused
 * codes**. Enrolling a *second* factor must not invalidate the ones written down for the
 * first, so `recovery_codes` is absent then. Check for the key; never index into it.
 */
export interface FactorConfirmed {
  detail: string;
  recovery_codes?: string[];
}

/**
 * What `POST /users/me/mfa/totp/enrol` hands back. The server mints the secret because it is
 * the party that has to write it into Keycloak and check the first code: a browser-chosen
 * secret would be the client declaring what its own second factor is.
 *
 * `otpauth_uri` goes straight to the QR renderer as an opaque string: do not parse it and do
 * not rebuild it from the parts. `secret` is the same thing in base32 for anyone who cannot
 * scan. Neither may reach logs, analytics, `localStorage` or the URL.
 */
export interface TotpEnrolment {
  /** Single-use, 10 minutes, bound to the signed-in account. Component state only. */
  enrolment_ticket: string;
  otpauth_uri: string;
  secret: string;
  issuer: string;
  account: string;
  /** Informational: the code length and the step, for the input and the countdown. */
  digits: number;
  period: number;
  expires_in: number;
}

// ---------------------------------------------------------------------------
// Recovery: all three unauthenticated
// ---------------------------------------------------------------------------

/**
 * **404 when the address holds no account.** This used to be a constant 202 whatever it found,
 * and the backend gave that up on purpose: the price of never confirming who holds an account
 * was that a mistyped address looked exactly like a real one, and the person who typed it went
 * off to wait for mail that could not arrive. So callers have to branch, and must not advance
 * on a 404.
 *
 * **503 is the account lookup failing**, not an answer about the address. Never render it as
 * "no such account": the account may well exist and the directory was simply unreachable.
 *
 * What a *real* address still reveals nothing about: whether a relay is configured, whether the
 * mail arrived, and whether this call minted a code or was swallowed by the server's 60-second
 * per-address cooldown. All three answer the same 202, hence the client-side resend timer.
 *
 * Rate limited to 5/hour/IP (429), which is low enough that a few mistyped addresses reach it.
 */
export const forgotPassword = (email: string) =>
  axios.post<{ detail: string }>(`${AUTH_BASE_URL}forgot-password`, { email });

/**
 * Five attempts, then the request is burned and **the correct code stops working too**: a
 * 429 here is terminal for this code, so send the user back to step 1 rather than letting
 * them keep trying. The code is a string: leading zeros are significant.
 */
export const forgotPasswordVerify = (email: string, code: string) =>
  axios.post<ForgotPasswordVerifyResponse>(`${AUTH_BASE_URL}forgot-password/verify`, {
    email,
    code
  });

/** On success every existing session for that account is revoked server-side. */
export const forgotPasswordConfirm = (
  resetTicket: string,
  newPassword: string,
  confirmPassword: string
) =>
  axios.post<{ detail: string }>(`${AUTH_BASE_URL}forgot-password/confirm`, {
    reset_ticket: resetTicket,
    new_password: newPassword,
    confirm_password: confirmPassword
  });

// ---------------------------------------------------------------------------
// MFA settings. Authenticated
// ---------------------------------------------------------------------------

export const getMyMfa = () =>
  axios.get<MfaStatus>(`${AUTH_BASE_URL}users/me/mfa`, { 
    headers: authHeaders() 
  });

/**
 * Begins authenticator-app enrolment and **turns nothing on**.  Safe to call repeatedly: each call issues a fresh secret and invalidates
 * the one before it, so a user who abandoned a QR and came back is not stuck. 5/minute.
 *
 * Enrolment used to go through Keycloak: `PUT /users/me/handle-mfa {"enable": true}` asked it to
 * email a setup link, which needs realm SMTP and this app's URL on the `account` client's
 * redirect allowlist. Neither is set on any SeeWard deployment, so it answered 500 and the panel
 * said authenticator setup was unavailable. This path involves no Keycloak configuration at all
 * and therefore behaves the same on a customer VM as in the cloud. The old route now answers 400
 * pointing here.
 *
 * `409` means an authenticator is already configured. Offer removal rather than a second QR.
 */
export const enrolTotp = () =>
  axios.post<TotpEnrolment>(
    `${AUTH_BASE_URL}users/me/mfa/totp/enrol`,
    {},
    { headers: authHeaders() }
  );

/**
 * Finishes enrolment: the server checks the code against the ticket's secret and only then
 * writes the credential to Keycloak. Until this returns 200 the factor is off, whatever the
 * screen shows.
 *
 * Its two `400`s mean opposite things and must not be collapsed into one message. See
 * `isEnrolmentExpired`.
 *
 * Returns the recovery-code set when this is the account's first factor. See
 * {@link FactorConfirmed}. That is the set's usual origin: the popup's Generate button exists
 * for the accounts that enrolled before recovery codes did.
 */
export const confirmTotp = (enrolmentTicket: string, code: string) =>
  axios.post<FactorConfirmed>(
    `${AUTH_BASE_URL}users/me/mfa/totp/confirm`,
    { enrolment_ticket: enrolmentTicket, code },
    { headers: authHeaders() }
  );

/** Takes no code: the session is the proof. */
export const disableTotp = () =>
  axios.delete<{ detail: string }>(`${AUTH_BASE_URL}users/me/mfa/totp`, {
    headers: authHeaders()
  });

// ---------------------------------------------------------------------------
// Recovery codes
// ---------------------------------------------------------------------------

/**
 * Ten single-use codes, `XXXX-XXXX`, from an alphabet without `0`/`O` or `1`/`I`/`L` because
 * they get written on paper and typed back weeks later.
 *
 * They exist for the day the authenticator is lost: the only self-service route left
 * before an administrator has to open the Keycloak console.
 */

/**
 * A count, never the codes. They are stored as salted digests and are **not recoverable, even
 * for their owner**: a set that could be re-read on demand would be a permanent second-factor
 * bypass sitting behind nothing but a session. Lost the paper means generating a new set, and
 * the UI has to say so or users hunt for a "show my codes" button that cannot exist.
 */
export const getRecoveryCodesStatus = () =>
  axios.get<RecoveryCodesStatus>(`${AUTH_BASE_URL}users/me/mfa/recovery-codes`, {
    headers: authHeaders()
  });

/**
 * Generate, or regenerate, which **retires the previous set immediately**, so warn before
 * submitting: anyone holding the old printout is holding dead codes.
 *
 * Re-authentication is required even though the caller already holds a valid token, because
 * this route mints a lasting way past the second factor and a stolen session must not be able
 * to walk off with ten codes that survive the owner changing their password.
 *
 * `otp` is **mandatory when the account has an authenticator**. Read {@link getMyMfa} to know.
 * Omitting it then answers 400 *"Enter the code from your authenticator app as well."*; a
 * wrong password or a wrong `otp` answers 403 *"Those credentials are not correct."*
 */
export const generateRecoveryCodes = (password: string, otp?: string) =>
  axios.post<{ recovery_codes: string[]; total: number }>(
    `${AUTH_BASE_URL}users/me/mfa/recovery-codes`,
    otp ? { password, otp } : { password },
    { headers: authHeaders() }
  );

// ---------------------------------------------------------------------------
// Signing in without the authenticator: both unauthenticated
// ---------------------------------------------------------------------------

/**
 * Both routes below still require the **password**: what they replace is the second factor,
 * not the first. And both **remove the authenticator**, because Keycloak refuses to issue
 * tokens without a valid TOTP code and the backend cannot produce one. It does not hold the
 * secret. So getting in without the app means the app stops being the factor, which is why
 * both answer with `reenrol_totp_required: true`.
 */

/**
 * Step 1 of the emailed fallback. It **cannot** verify the password first: the grant that
 * would do it is the one TOTP is blocking. Rate limited to 5/hour by IP, with a 60-second
 * per-address cooldown that is invisible: a call inside it still answers 202.
 *
 * It answers the same four ways `/forgot-password` does, and stopped being a constant 202 for
 * the same reason. **A 404 means something different here**, though. The caller reached this
 * route from a sign-in that had just been answered with a second-factor challenge, so the
 * address is one the server recognised moments ago and is not something the user typed. Treat
 * it as the account being gone, not as a typo to correct: there is nothing to retry.
 *
 * A 503 is the lookup failing and says nothing about the address. Never render it as "no
 * account".
 */
export const sendLoginFallbackCode = (email: string) =>
  axios.post<{ detail: string }>(`${AUTH_BASE_URL}login/fallback/send`, { 
    email 
  });

/**
 * Step 2. The password comes back **with** the code because the client still holds it in
 * memory. Nothing is parked server-side between the two calls.
 *
 * ⚠ If the emailed code is right but the password is wrong, the authenticator has **already
 * been cleared**. That is not a way in: the account is still behind its password, but the
 * user has to re-enrol afterwards, so the screen confirms before submitting.
 */
export const loginFallback = (email: string, password: string, code: string) =>
  axios.post<LoginResponse>(`${AUTH_BASE_URL}login/fallback`, {
    email,
    password,
    code
  });

/**
 * One 401 for every failure. Wrong code, unknown account, spent code. Deliberate: it must not
 * reveal which. Show "That code is not valid" and let them retry.
 */
export const loginRecovery = (
  email: string,
  password: string,
  recoveryCode: string
) =>
  axios.post<LoginResponse>(`${AUTH_BASE_URL}login/recovery`, {
    email,
    password,
    recovery_code: recoveryCode
  });

/**
 * The hyphen is cosmetic and case does not matter: the backend normalises, so accept
 * whatever the user types and send the stripped, upper-cased form.
 */
export const normaliseRecoveryCode = (raw: string): string =>
  raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

/** `m2mchgsc` → `M2MC-HGSC`, for the input's own display while typing. */
export const formatRecoveryCode = (raw: string): string => {
  const clean = normaliseRecoveryCode(raw).slice(0, 8);
  return clean.length > 4 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean;
};

// ---------------------------------------------------------------------------
// Passwords
// ---------------------------------------------------------------------------

/**
 * Change your own password while signed in.
 *
 * Replaces the frontend's old `PUT .../auth/user/{id}`, which is not a route the API serves:
 * the Profile button 404'd on every click.
 */
export const resetOwnPassword = (
  email: string,
  password: string,
  confirmPassword: string
) =>
  axios.post<{ detail?: string }>(
    `${AUTH_BASE_URL}users/reset-password`,
    {
      email,
      password,
      confirm_password: confirmPassword
    },
    { headers: authHeaders() }
  );

/**
 * Change your own display name, and nothing else. The account is the one in the token; the
 * body carries no address. Replaces `POST /users/update-profile`, which set a new password
 * in the same call and required one, so saving a name used to reset the password too.
 */
export const updateOwnName = (firstName: string, lastName: string) =>
  axios.put<{ detail?: string; first_name: string; last_name: string }>(
    `${AUTH_BASE_URL}users/me/profile`,
    { first_name: firstName, last_name: lastName },
    { headers: authHeaders() }
  );

// ---------------------------------------------------------------------------
// Error wording
// ---------------------------------------------------------------------------

/**
 * The server's `detail`, flattened. FastAPI sends a string for handled errors and an array of
 * `{ msg }` for validation failures; `/login` alone uses `message` instead of `detail`.
 */
export const serverDetail = (error: any): string | null => {
  const data = error?.response?.data;
  if (!data) return null;
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) {
    return data.detail.map((d: any) => d?.msg ?? String(d)).join('\n');
  }
  if (typeof data.message === 'string') return data.message;
  return null;
};

export const statusOf = (error: any): number | undefined => error?.response?.status;

/**
 * A backing service being down must never read as a credential problem: the second factor
 * fails closed rather than letting anyone in.
 */
export const UNAVAILABLE =
  'This is temporarily unavailable. Please try again shortly.';

/**
 * Both mailed-code routes an anonymous caller can reach, `/forgot-password` and
 * `/login/fallback/send`, are limited to 5 an hour per IP. That is low enough to reach by
 * accident: two mistyped addresses, a resend, and a colleague on the same office connection
 * will do it. So the wording says how long the wait is rather than leaving somebody clicking
 * a button that cannot work yet.
 */
export const TOO_MANY_REQUESTS_HOURLY =
  'Too many attempts. This is limited to five requests an hour from one network. Try again later.';

/**
 * `POST .../totp/confirm` answers 400 for two unrelated failures, and the recovery differs:
 *
 * - *"That code is not correct…"*. Keep the QR and the ticket, let them try again.
 * - *"Invalid or expired enrolment. Start again."*: the ticket is dead; fetch a fresh QR.
 *
 * Showing the second as a retry is what dead-ends a user: they would retype codes against a
 * secret the server has already forgotten.
 */
export const isEnrolmentExpired = (error: any): boolean => {
  if (statusOf(error) !== 400) return false;
  const detail = (serverDetail(error) ?? '').toLowerCase();
  return detail.includes('start again') || detail.includes('expired enrolment');
};

export const messageFor = (
  error: any,
  fallbacks: Record<number, string>,
  last = 'Something went wrong. Please try again.'
): string => {
  const status = statusOf(error);
  if (status === undefined) {
    return 'Cannot reach the server. Check your connection and try again.';
  }
  if (status === 503) return serverDetail(error) || UNAVAILABLE;
  return serverDetail(error) || fallbacks[status] || last;
};

export default {
  forgotPassword,
  forgotPasswordVerify,
  forgotPasswordConfirm,
  getMyMfa,
  enrolTotp,
  confirmTotp,
  disableTotp,
  getRecoveryCodesStatus,
  generateRecoveryCodes,
  sendLoginFallbackCode,
  loginFallback,
  loginRecovery,
  readPostLoginFlags,
  clearPostLoginFlag,
  resetOwnPassword,
  updateOwnName
};
